import React, { useEffect, useState, useCallback } from "react"
import CommonTable, { type TableColumn } from "@/components/common/CommonTable"
import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { Button } from "@/components/ui/button"
import { Download, Eye, Lock, MoreHorizontal, RefreshCw, Upload, Users, UserX, Calendar, Clock } from "lucide-react"
import { useAppSelector } from "@/store/store"
import { AttendanceService } from "@/services/Attendance/AttendanceService"
import { ThrowError } from "@/api/callApi"
import moment from "moment"
import type { IAttendanceDetail, IAttendanceOverview } from "@/interface/attendance.interface"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { RoutesLink } from "@/utility/RouteLinks"
import { useNavigate } from "react-router-dom"
import CommonFilter from "@/components/common/CommonFilter"
import type { IFilter } from "@/interface/common.interface"

const MyAttendance: React.FC = () => {
    const loggedInUser = useAppSelector((state) => state.user.user)
    const [attendanceList, setAttendanceList] = useState<IAttendanceDetail[]>([])
    const [overview, setOverview] = useState<IAttendanceOverview | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate();

    const [filter, setFilter] = useState<IFilter>({
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
    })

    const handleDateRangeChange = useCallback((start: string, end: string) => {
        setFilter((prev) => ({ ...prev, startDate: start, endDate: end }))
    }, [])

    const fetchAttendance = async () => {
        if (!loggedInUser?.id) return
        setIsLoading(true)
        try {
            let params = `userId=${loggedInUser.id}`
            if (filter.startDate) {
                params += `&startDate=${filter.startDate}`
            }
            if (filter.endDate) {
                params += `&endDate=${filter.endDate}`
            }

            const response = await AttendanceService.getAttendance(params)
            if (response?.status && response?.data?.attendance) {
                setAttendanceList(response?.data?.attendance)
            }

            const overviewResponse = await AttendanceService.getAttendanceOverview(params)
            console.log("overviewResponse?.data", overviewResponse?.data)
            if (overviewResponse?.status && overviewResponse?.data) {
                setOverview(overviewResponse.data)
            }
        } catch (error) {
            ThrowError(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchAttendance()
    }, [loggedInUser?.id, filter.startDate, filter.endDate])

    const formatTime = (timeStr: string | null) => {
        if (!timeStr) return '--:--'
        if (timeStr.includes('T') || timeStr.includes('-')) {
            return moment.utc(timeStr).format('hh:mm A')
        }
        return moment(timeStr, 'HH:mm:ss').format('hh:mm A')
    }

    const calculateTotalHours = (checkIn: string | null, checkOut: string | null) => {
        if (!checkIn) return '--:--'
        const getMomentObj = (timeStr: string) => {
            if (timeStr.includes('T') || timeStr.includes('-')) {
                return moment.utc(timeStr)
            }
            return moment(timeStr, 'HH:mm:ss')
        }
        const start = getMomentObj(checkIn)
        const end = checkOut ? getMomentObj(checkOut) : moment()
        const duration = moment.duration(end.diff(start))
        const hours = Math.floor(duration.asHours())
        const minutes = Math.floor(duration.minutes())
        if (hours < 0 || minutes < 0 || isNaN(hours) || isNaN(minutes)) return '00:00'
        const pad = (num: number) => String(num).padStart(2, '0')
        return `${pad(hours)}:${pad(minutes)}`
    }

    const columns: TableColumn<IAttendanceDetail>[] = [
        {
            key: "srNo",
            header: "Sr No",
            render: (_item, index) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },

        {
            key: "date",
            header: "Date",
            render: (item) => (
                <span className="font-semibold">{item?.date ? moment(item.date).format("DD-MM-YYYY") : "—"}</span>
            )
        },
        {
            key: "checkInTime",
            header: "Check In Time",
            render: (item) => (
                <span className="font-medium text-slate-700">{item?.checkInTime ? formatTime(item.checkInTime) : "—"}</span>
            )
        },
        {
            key: "checkOutTime",
            header: "Check Out Time",
            render: (item) => (
                <span className="font-medium text-slate-700">{item?.checkOutTime ? formatTime(item.checkOutTime) : "—"}</span>
            )
        },
        {
            key: "totalHours",
            header: "Working Hours",
            render: (item) => (
                <span className="font-semibold text-emerald-600">
                    {calculateTotalHours(item?.checkInTime, item?.checkOutTime)}
                </span>
            )
        },
        {
            key: "status",
            header: "Status",
            render: (item) => {
                const status = (item?.status || "PRESENT").toLowerCase()
                let statusClasses = "text-amber-600 border-amber-600 bg-amber-50"
                if (status === "present") {
                    statusClasses = "text-green-600 border-green-600 bg-green-50"
                } else if (status === "absent") {
                    statusClasses = "text-destructive border-destructive bg-destructive/10"
                } else if (status === "leave") {
                    statusClasses = "text-blue-600 border-blue-600 bg-blue-50"
                } else if (status === "late") {
                    statusClasses = "text-orange-600 border-orange-600 bg-orange-50"
                }
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold border rounded-full capitalize ${statusClasses}`}>
                        {status}
                    </span>
                )
            }
        },

        {
            key: "remarks",
            header: "Remarks",
            render: (item) => (
                <span className="text-slate-500 text-sm font-medium">{item?.remarks || "—"}</span>
            )
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: IAttendanceDetail) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4 text-slate-500" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[170px] rounded-xl shadow-lg border-slate-100">
                        <DropdownMenuItem
                            onClick={() => {
                                console.log("View Employee Clicked - RoutesLink.employeeDetail:", RoutesLink.attendanceLog);
                                navigate(`${RoutesLink.attendanceLog}`, {
                                    state: {
                                        date: item.date,

                                    }
                                });
                            }}
                            className="cursor-pointer py-2 px-3 hover:bg-slate-50 focus:bg-slate-50"
                        >
                            <Eye className="h-4 w-4" />View Log
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },

    ]

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex justify-between">
                    <MainHeading title="My Attendance Records" />
                    <div>
                        <div className="flex gap-2">
                            <Button variant="outline" className="bg-white"> <Download /> Export</Button>
                            <Button variant="outline" className="bg-white"><Upload /> Import</Button>
                            <Button onClick={fetchAttendance} disabled={isLoading}><RefreshCw className={isLoading ? "animate-spin" : ""} /> Sync Biometric</Button>
                            <Button className="bg-green-600 hover:bg-green-700 "><Lock /> Lock Attendance</Button>
                        </div>
                    </div>
                </div>

                {/* Overview Cards Grid */}
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center">
                                <Users className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-slate-500">Present:</div>
                                <div className="text-2xl font-bold text-slate-900">{overview?.totalPresent ?? 0}</div>
                            </div>
                        </div>
                        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center">
                                <UserX className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-slate-500">Absent:</div>
                                <div className="text-2xl font-bold text-slate-900">{overview?.totalAbsent ?? 0}</div>
                            </div>
                        </div>
                        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center">
                                <Calendar className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-slate-500">Leaves:</div>
                                <div className="text-2xl font-bold text-slate-900">{overview?.totalLeave ?? 0}</div>
                            </div>
                        </div>
                        <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary text-white flex items-center justify-center">
                                <Clock className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="text-xs font-semibold text-slate-500">Percentage:</div>
                                <div className="text-2xl font-bold text-slate-900">
                                    {overview?.attendancePercentage !== undefined ? `${overview.attendancePercentage}%` : "0%"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Common Filter with Date Range */}
                <CommonFilter
                    startDate={filter.startDate || ""}
                    endDate={filter.endDate || ""}
                    onDateRangeChange={handleDateRangeChange}
                />

                <CommonTable columns={columns} data={attendanceList} loading={isLoading} />
            </div>
        </Layout>
    )
}

export default MyAttendance